import React, { useState, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  LinearProgress,
  Card,
  CardContent,
  Stack
} from '@mui/joy';
import {
  UploadSimple as Upload,
  FileText,
  CheckCircle,
  Files as Pages,
  XCircle as AlertCircle
} from '@phosphor-icons/react';
import type { Contract, PdfPageInfo } from '../../../types';
import { api } from '../../../lib/api';

interface PDFUploadProps {
  contract: Contract;
  isTemplate?: boolean;
  templateId?: number;
  onUploadSuccess?: (contract: Contract, pages: PdfPageInfo[]) => void;
  onUploadError?: (error: string) => void;
  /** Opens the per-page editor. Omitted when page editing isn't available. */
  onManagePages?: () => void;
  disabled?: boolean;
}

export const PDFUpload: React.FC<PDFUploadProps> = ({
  contract,
  isTemplate = false,
  templateId,
  onUploadSuccess,
  onUploadError,
  onManagePages,
  disabled = false
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  const validateFile = useCallback((file: File): string | null => {
    if (!file.type.includes('pdf')) {
      return 'Please select a PDF file.';
    }
    
    if (file.size > 50 * 1024 * 1024) { // 50MB limit
      return 'File size must be less than 50MB.';
    }
    
    return null;
  }, []);

  const uploadFile = useCallback(async (file: File) => {
    if (disabled) return;
    
    const validationError = validateFile(file);
    if (validationError) {
      onUploadError?.(validationError);
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setUploadedFile(file);

    try {
      let response;
      
      if (isTemplate && templateId) {
        // Upload for template
        response = await api.uploadContractTemplatePdf(templateId, file);
      } else {
        // Upload for contract
        response = await api.uploadCrmContractPdf(contract.id, file);
      }

      if (response.status !== 200) {
        // Extract detailed error message from response
        const errorData = response.data;
        let errorMessage = 'Upload failed';
        
        if (errorData) {
          if (typeof errorData === 'string') {
            errorMessage = errorData;
          } else if (errorData.error) {
            errorMessage = errorData.error;
          } else if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.detail) {
            errorMessage = errorData.detail;
          } else if (errorData.non_field_errors) {
            errorMessage = Array.isArray(errorData.non_field_errors) 
              ? errorData.non_field_errors.join(', ')
              : errorData.non_field_errors;
          } else if (errorData.pdf_file) {
            errorMessage = Array.isArray(errorData.pdf_file)
              ? `PDF file: ${errorData.pdf_file.join(', ')}`
              : `PDF file: ${errorData.pdf_file}`;
          } else {
            // Try to extract any field errors
            const fieldErrors = Object.entries(errorData)
              .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
              .join('; ');
            if (fieldErrors) {
              errorMessage = fieldErrors;
            }
          }
        }
        
        console.error('PDF upload error:', {
          status: response.status,
          message: response.message,
          data: errorData
        });
        
        throw new Error(errorMessage);
      }

      const result = response.data;

      // Point at the same authenticated endpoint the serializer returns rather than
      // an object URL: everything downstream re-fetches pdf_url with a Bearer token,
      // and a blob: URL is neither authenticated nor valid after a reload.
      // The cache-buster makes "Replace" show the new file on the unchanged path.
      const basePath = isTemplate && templateId
        ? `/api/crm/contract-templates/${templateId}/view_pdf/`
        : `/api/crm/contracts/${contract.id}/view_pdf/`;

      const updatedContract: Contract = {
        ...contract,
        pdf_url: `${basePath}?v=${Date.now()}`,
        pdf_pages: result.page_info
      };

      onUploadSuccess?.(updatedContract, result.page_info);
    } catch (error: any) {
      console.error('PDF upload exception:', error);
      
      // Extract error message from various possible sources
      let errorMessage = 'Upload failed. Please check the file format and try again.';
      
      if (error instanceof Error) {
        errorMessage = error.message;
        
        // Check if error has responseData attached (from API layer)
        if ((error as any).responseData) {
          const responseData = (error as any).responseData;
          if (responseData.detail) {
            errorMessage = responseData.detail;
          } else if (responseData.error) {
            errorMessage = typeof responseData.error === 'string'
              ? responseData.error
              : JSON.stringify(responseData.error);
          } else if (responseData.message) {
            errorMessage = responseData.message;
          } else if (responseData.pdf_file) {
            errorMessage = Array.isArray(responseData.pdf_file)
              ? `PDF file: ${responseData.pdf_file.join(', ')}`
              : `PDF file: ${responseData.pdf_file}`;
          } else if (typeof responseData === 'object') {
            // Try to extract field errors
            const fieldErrors = Object.entries(responseData)
              .filter(([key]) => key !== 'status' && key !== 'statusText')
              .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
              .join('; ');
            if (fieldErrors) {
              errorMessage = fieldErrors;
            }
          }
        }
      } else if (typeof error === 'string') {
        errorMessage = error;
      }
      
      onUploadError?.(errorMessage);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  }, [contract, templateId, isTemplate, disabled, validateFile, onUploadSuccess, onUploadError]);

  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Clear the input so picking the same filename again still fires onChange -
    // re-uploading a corrected copy under the same name is the common case.
    event.target.value = '';
    if (file) {
      uploadFile(file);
    }
  }, [uploadFile]);

  const handleDrop = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(false);
    
    const file = event.dataTransfer.files[0];
    if (file) {
      uploadFile(file);
    }
  }, [uploadFile]);

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(false);
  }, []);

  const fileInput = (
    <input
      ref={fileInputRef}
      type="file"
      accept=".pdf"
      onChange={handleFileSelect}
      style={{ display: 'none' }}
      disabled={disabled || uploading}
    />
  );

  const openFilePicker = () => fileInputRef.current?.click();

  // If contract already has a PDF, show success state. The picker has to be
  // mounted here too - "Replace" used to reach for an input that only existed
  // in the empty state, so the button did nothing at all.
  if (contract.pdf_url && !uploading) {
    return (
      <Card variant="outlined" color="success">
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center">
            <CheckCircle size={24} color="var(--joy-palette-success-500)" />
            <Box sx={{ flex: 1 }}>
              <Typography level="title-sm" color="success">
                PDF Document Uploaded
              </Typography>
              <Typography level="body-sm" color="success">
                {contract.pdf_pages?.length || 0} pages • Ready for field placement
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              {onManagePages && (
                <Button
                  size="sm"
                  variant="outlined"
                  color="success"
                  startDecorator={<Pages size={16} />}
                  onClick={onManagePages}
                  disabled={disabled}
                >
                  Manage Pages
                </Button>
              )}
              <Button
                size="sm"
                variant="outlined"
                color="success"
                startDecorator={<Upload size={16} />}
                onClick={openFilePicker}
                disabled={disabled}
              >
                Replace All
              </Button>
            </Stack>
          </Stack>
          {fileInput}
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      {/* Hidden file input */}
      {fileInput}

      {/* Drop zone */}
      <Card
        variant="outlined"
        sx={{
          border: dragOver ? '2px dashed var(--joy-palette-primary-300)' : '2px dashed var(--joy-palette-neutral-300)',
          bgcolor: dragOver ? 'background.level1' : 'background.surface',
          cursor: disabled || uploading ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': {
            borderColor: !disabled && !uploading ? 'primary.300' : undefined,
            bgcolor: !disabled && !uploading ? 'background.level1' : undefined,
          }
        }}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => !disabled && !uploading && openFilePicker()}
      >
        <CardContent sx={{ textAlign: 'center', py: 6 }}>
          {uploading ? (
            <Stack spacing={3} alignItems="center">
              <Box sx={{ width: '100%', maxWidth: 400 }}>
                <LinearProgress
                  determinate
                  value={uploadProgress > 0 ? uploadProgress : undefined}
                  sx={{ mb: 2 }}
                />
                <Typography level="body-sm" textColor="text.secondary">
                  {uploadedFile ? `Uploading ${uploadedFile.name}...` : 'Processing...'}
                </Typography>
              </Box>
            </Stack>
          ) : (
            <Stack spacing={3} alignItems="center">
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  bgcolor: 'primary.50',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <FileText size={32} color="var(--joy-palette-primary-500)" />
              </Box>
              
              <Stack spacing={1} alignItems="center">
                <Typography level="title-md">
                  Upload PDF Document
                </Typography>
                <Typography level="body-sm" textColor="text.secondary">
                  Drag and drop your PDF file here, or click to browse
                </Typography>
                <Typography level="body-xs" textColor="text.tertiary">
                  Maximum file size: 50MB
                </Typography>
              </Stack>

              <Button
                variant="solid"
                color="primary"
                startDecorator={<Upload size={16} />}
                disabled={disabled || uploading}
              >
                Select PDF File
              </Button>
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* Error display */}
      {!uploading && (
        <Box sx={{ mt: 2 }}>
          <Typography level="body-xs" textColor="text.tertiary">
            <AlertCircle size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />
            Once uploaded, you'll be able to place signature fields, text inputs, and other form elements on your PDF.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default PDFUpload;